import React from 'react';
import { confidenceOpacity } from '../../shared/format';
import AnswerActions from './AnswerActions';
import { PILL } from '../liStyles';

// One answer: the line to say out loud (verbatim — never rebuilt from other fields),
// why it fits, how fresh the hours are, whether it is ready to send guests, and every
// action. Tapping the body opens the place. `confidence` is a styling key only:
// unverified answers keep their line at full strength and quiet the evidence.
const AnswerCard = ({ answer, index, compact = false, active = false, onOpen, onBook, onOutcome }) => {
  if (!answer) return null;
  const reasons = Array.isArray(answer.reasons) ? answer.reasons : [];
  const muted = confidenceOpacity(answer.confidence === 'unverified' ? 'low' : 'high');
  // Item 2, Step 2 — condense the meta chips for the narrower right column. The
  // answer's `reasons` are three pre-built strings (open-until, drive time,
  // rating) plus occasional extras ("Guests report long waits"). Fold drive time
  // + rating into one chip ("8 min drive · 4.4") so the row fits one line;
  // open-until keeps its own chip. `hours_label` / `distance_label` are the
  // structured source of truth; the rating number is pulled from the reasons
  // string because answer results carry no structured rating field.
  const RATING_RE = /^([\d.]+)\s+from\s+[\d,]+\s+reviews/i;
  const ratingReason = reasons.find((r) => RATING_RE.test(r));
  const ratingNum = ratingReason ? ratingReason.match(RATING_RE)[1] : null;
  const driveRatingChip = [answer.distance_label, ratingNum].filter(Boolean).join(' · ');
  // Drop any reason already represented (open-until, drive, rating) so extras
  // don't repeat what the condensed chips show.
  const consumed = new Set([ratingReason, answer.hours_label, answer.distance_label]);
  const extraChips = reasons.filter((r) => !consumed.has(r));
  // Item 3 — fold the "Quietest" hint into the meta chips row so it reads as a
  // scannable suggestion instead of a stray line. The backend ships the full
  // string ("Quietest: Sunday around 7 AM"); compact it to "Quietest Sun 7 AM"
  // for the chip (strip the colon, abbreviate the day, drop "around", keep the
  // time verbatim). If the format ever changes, fall back to the raw label so
  // the hint still renders. Returns '' when best_time_label is absent, so the
  // .filter(Boolean) on metaChips drops it entirely — no empty slot.
  const BEST_TIME_RE = /^Quietest:\s+(\w+)\s+around\s+(.+)$/i;
  const DAY_ABBR = { sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' };
  const bestTimeChip = (() => {
    if (!answer.best_time_label) return '';
    const m = String(answer.best_time_label).match(BEST_TIME_RE);
    if (!m) return String(answer.best_time_label);
    const day = DAY_ABBR[m[1].toLowerCase()] || m[1];
    return `Quietest ${day} ${m[2]}`;
  })();
  const metaChips = [answer.hours_label, driveRatingChip, bestTimeChip, ...extraChips].filter(Boolean);
  // Item 2 — clarify the data-freshness label. The backend ships the full
  // string ("Hours checked Sep 27"); strip that prefix and re-render with a
  // clearer one so a front-desk user knows *what* was verified and when. The
  // date value is preserved verbatim (no parsing/reformat). If the field ever
  // stops matching the known prefix, fall back to the raw label as-is.
  const HOURS_CHECKED_PREFIX = /^Hours checked\s+/i;
  const rawChecked = answer.hours_checked_label ? String(answer.hours_checked_label) : '';
  const checkedMatch = rawChecked.match(HOURS_CHECKED_PREFIX);
  const hoursCheckedLabel = checkedMatch
    ? `Hours verified ${rawChecked.slice(checkedMatch[0].length)}`
    : rawChecked;
  const open = () => onOpen && onOpen(answer);
  // work-page-fixes Item 1 — solid neutral card surface. The panel these cards
  // sit on is bg-white dark:bg-gray-900 (CARD_LIST in liStyles.js), so the card
  // must not match it. Light mode uses gray-50 (the closest step to white,
  // visible but not stark). Dark mode uses a deep blue-dark surface (#1C2B40)
  // rather than a Tailwind gray step: the standard gray-800 was a blue-cast
  // slate that read as "washed fog," and #1C2B40 gives a clean dark surface so
  // the hierarchy reads page → surface → chip as real steps. On dark
  // backgrounds a fill alone doesn't sell "this is a card" — the border does
  // more of the work — so the dark border is a subtle teal accent (#1e3a4a),
  // clearly visible against #1C2B40 where the previous gray-600/700 border
  // nearly vanished into the fill, and the shadow is strengthened (shadow-md /
  // dark:shadow-lg) to add elevation. Dark hover lifts to #22344c (a step
  // above the base in the same blue-dark tone). The brand-blue hover/active
  // borders (#32B1FF at 50% / full) stay high-contrast against the dark fill,
  // so they remain clearly visible. Together fill + border + shadow give three
  // cues for "I am a card." The active state is marked by the brand-blue
  // border only; the background no longer tints blue. The Call button stays
  // the only saturated blue block on the card. NOTE: these are local hex
  // values, not shared tokens — if the app adopts a dark-mode token layer,
  // lift #1C2B40 / #1e3a4a / #22344c into it (see work-page-fixes tracker,
  // future session).
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
      data-testid="li-answer"
      className={`group rounded-lg border p-3 sm:p-4 text-left cursor-pointer transition-colors shadow-md dark:shadow-lg bg-gray-50 dark:bg-[#1C2B40] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50 ${
        active
          ? 'border-[#32B1FF]'
          : 'border-gray-200 dark:border-[#1e3a4a] hover:border-[#32B1FF]/50 dark:hover:bg-[#22344c]'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Number column widened from w-6 (24px) to w-10 (40px): the index renders
            at the .gi-number size (clamp 20→24px, 600 weight), so a 3-digit
            number is ~40-45px wide and overflowed w-6, crowding the content. w-10
            holds 3 digits, and gap-4 adds breathing room so even at the 24px
            viewport ceiling a wide "100" never sticks to the content. */}
        {index != null && (
          <span className="gi-number text-[#32B1FF] leading-none mt-0.5 w-10 shrink-0 text-center" aria-hidden="true">{index + 1}</span>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <p className={`${compact ? 'text-sm' : 'text-sm font-medium'} text-gray-800 dark:text-gray-200 mt-0.5`}>{answer.line}</p>
          </div>
          {!compact && metaChips.length > 0 && (
            <div className={`flex flex-wrap gap-1.5 ${muted}`}>
              {metaChips.map((r) => (
                <span key={r} className="text-sm px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 whitespace-nowrap">{r}</span>
              ))}
            </div>
          )}
          <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 ${muted}`}>
            {answer.guest_ready_label && (
              <span className={`whitespace-nowrap ${answer.guest_ready ? PILL.emerald : PILL.gray}`}>{answer.guest_ready_label}</span>
            )}
            {hoursCheckedLabel && <span className="whitespace-nowrap">{hoursCheckedLabel}</span>}
          </div>
          <AnswerActions answer={answer} onBook={onBook} onOutcome={onOutcome} size={compact ? 'sm' : 'md'} />
        </div>
      </div>
    </div>
  );
};

export default AnswerCard;
